// Work's Boards (`/work/boards`, was Home at `/work/home`; WORK-OS, the founder's "work is mainly docs, not a work OS with tasks
// and projects"): the overview of every board, the way Asana's portfolio and ClickUp's
// Everything read. The strip on top is the person's day (due today, overdue, due this week,
// open on their seats: `my.tasks`); the table under it is one row per board (`boards.summary`,
// one call): its coloured mark and space, its lead, a done/total progress bar, open and
// overdue counts, the next due date and a health chip, filtered by space and lead and sorted by
// any column; a row opens the board. "My work today" lists the day's and the overdue tasks
// with the inline editors, and "+ New board" offers the spec's templates with a space picker.
//
// BOARD-ORDER: unsorted, the table is the shared order (spaces in the company's order, boards in
// their space's). Then a row shows a grip on hover and drags before another row (another space's
// row moves it there); its "…" menu has Move up / down, Move to space, Colour and Lead. The mark
// opens the colour swatches and the Lead cell the lead picker; every write is optimistic.
import {
  AvatarStack,
  Badge,
  Button,
  cn,
  EmptyState,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Progress,
  SearchField,
  Skeleton,
  StatGrid,
  StatTile,
  TableView,
  type TableViewColumn,
  type TableViewSort,
  Toolbar,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  CalendarCheckIcon,
  CalendarClockIcon,
  CircleAlertIcon,
  EllipsisIcon,
  GripVerticalIcon,
  LayoutDashboardIcon,
  ListTodoIcon,
  PlusIcon,
  SquareKanbanIcon,
} from "lucide-react";
import { type DragEvent, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isOperatorOnly } from "../company";
import { OperatorOnlyNotice } from "../operator-only";
import { LoadError } from "../screen-parts";
import { BoardColorButton, LeadPicker } from "./board-controls";
import { byOrder, orderedSpaces } from "./board-order";
import { useBoardWrites } from "./board-writes";
import {
  addDays,
  boardHealth,
  isDone,
  localDay,
  myBucket,
  type SeatPeople,
  shortDay,
  useSeatPeople,
} from "./model";
import type { TaskCard } from "./view-engine";
import { BoardMenu, NewBoardDialog } from "./work-dialogs";
import {
  type BoardSummary,
  boardSummariesQuery,
  boardsQuery,
  myTasksQuery,
  spacesQuery,
} from "./work-queries";
import { BODY, TaskList } from "./work-screens";

export function WorkHome({
  api,
  company,
  onOpenTask,
}: {
  api: Api;
  company: CompanyAccess;
  onOpenTask: (id: string) => void;
}) {
  const co = company.id;
  const navigate = useNavigate();
  const people = useSeatPeople(api, co);
  const spaces = useQuery(spacesQuery(api, co));
  const boards = useQuery(boardsQuery(api, co));
  // The overview's counts are read fresh each time Home opens (the cached copy shows meanwhile):
  // a copy restored from this browser's cache can predate a write made just before a reload.
  const summaries = useQuery({ ...boardSummariesQuery(api, co), refetchOnMount: "always" });
  // OPERATOR-ONLY-UX: no person membership, so no tasks of one's own (no seats); one notice says
  // why. Boards and tasks stay: the operator makes them in an existing space (company#admin).
  const operatorOnly = isOperatorOnly(company);
  const mine = useQuery({ ...myTasksQuery(api, co), enabled: !operatorOnly });
  const [search, setSearch] = useState("");
  const [spaceId, setSpaceId] = useState("");
  const [lead, setLead] = useState("");
  const [creating, setCreating] = useState(false);
  // BOARD-ORDER: no column sort reads the shared order (and allows dragging).
  const [sort, setSort] = useState<TableViewSort>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropBefore, setDropBefore] = useState<string | null>(null);
  const writes = useBoardWrites(api, company);
  const today = localDay(new Date());
  const spaceList = spaces.data?.data ?? [];
  const boardList = boards.data?.data ?? [];
  const spaceOf = (id: string) => spaceList.find((s) => s.id === id);
  const boardOf = (id: string) => boardList.find((b) => b.id === id);

  // The day: the caller's open tasks by section.
  const open = (mine.data?.data ?? []).filter((t) => !isDone(t, boardOf(t.board_id)));
  const bucket = (t: TaskCard) => myBucket(t.due_at, today);
  const dueToday = open.filter((t) => bucket(t) === "today");
  const overdue = open.filter((t) => bucket(t) === "overdue");
  const dueWeek = open.filter((t) => {
    const b = bucket(t);
    return b === "today" || b === "week";
  });

  const spaceRank = useMemo(
    () => new Map(orderedSpaces(spaceList).map((s, i) => [s.id, i] as const)),
    [spaceList],
  );
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (summaries.data ?? [])
      .filter((s) => !s.archived_at)
      .sort(
        (a, b) =>
          (spaceRank.get(a.space_id) ?? 1e9) - (spaceRank.get(b.space_id) ?? 1e9) ||
          byOrder({ ...a, id: a.board_id }, { ...b, id: b.board_id }),
      )
      .filter((s) => !spaceId || s.space_id === spaceId)
      .filter((s) => !lead || leadOf(s) === lead)
      .filter((s) => !q || s.name.toLowerCase().includes(q));
  }, [summaries.data, search, spaceId, lead, spaceRank]);
  const leads = useMemo(() => {
    const ids = new Set(
      (summaries.data ?? []).flatMap((s) => {
        const l = leadOf(s);
        return l ? [l] : [];
      }),
    );
    return [...ids]
      .map((id) => ({ id, name: people.of(id).name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [summaries.data, people]);

  const columns: TableViewColumn<BoardSummary>[] = [
    {
      id: "board",
      header: "Board",
      accessor: (s) => s.name,
      sortable: true,
      className: "min-w-56",
      // BOARD-ORDER: the grip and the colour swatches sit before the row's open button.
      prefix: (s) => (
        <>
          {sort === null ? (
            <GripVerticalIcon
              aria-hidden="true"
              data-slot="board-grip"
              className="-ml-1 size-3.5 shrink-0 cursor-grab text-faint opacity-0 group-hover/board-row:opacity-100 pointer-coarse:hidden"
            />
          ) : null}
          <BoardColorButton
            board={{ id: s.board_id, name: s.name, color: s.color }}
            onPick={(color) => writes.color({ id: s.board_id }, color)}
          />
        </>
      ),
      cell: (s) => (
        <span className="inline-flex min-w-0 items-center gap-2 align-middle">
          <span className="min-w-0">
            <span className="block truncate font-medium text-foreground">{s.name}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {spaceOf(s.space_id)?.name ?? ""}
            </span>
          </span>
        </span>
      ),
    },
    {
      id: "lead",
      header: "Lead",
      accessor: (s) => {
        const l = leadOf(s);
        return l ? people.of(l).name : null;
      },
      sortable: true,
      className: "w-44",
      cell: (s) => (
        <LeadPicker
          people={people}
          seatId={leadOf(s)}
          boardName={s.name}
          quiet
          onPick={(seat) => writes.lead({ id: s.board_id }, seat)}
        />
      ),
    },
    {
      id: "progress",
      header: "Progress",
      accessor: (s) => (s.total ? s.done / s.total : 0),
      sortable: true,
      className: "w-44",
      cell: (s) => (
        <span className="flex items-center gap-2">
          <Progress
            value={s.total ? Math.round((s.done / s.total) * 100) : 0}
            aria-label={`${s.done} of ${s.total} tasks done`}
            className="w-20 flex-nowrap"
          />
          <span className="text-xs text-muted-foreground tabular-nums">
            {s.done}/{s.total}
          </span>
        </span>
      ),
    },
    { id: "open", header: "Open", accessor: (s) => s.open, sortable: true, numeric: true },
    {
      id: "overdue",
      header: "Overdue",
      accessor: (s) => s.overdue,
      sortable: true,
      numeric: true,
      cell: (s) =>
        s.overdue ? (
          <span className="font-medium text-danger-text">{s.overdue}</span>
        ) : (
          <span className="text-faint">0</span>
        ),
    },
    {
      id: "next_due",
      header: "Next due",
      accessor: (s) => s.next_due_at,
      sortable: true,
      className: "w-24",
      cell: (s) =>
        s.next_due_at ? (
          <span className="tabular-nums">{nextDueLabel(s.next_due_at, today)}</span>
        ) : (
          <span className="text-faint">—</span>
        ),
    },
    {
      id: "people",
      header: "People",
      accessor: (s) => s.assignee_seat_ids.length,
      className: "w-28",
      cell: (s) => <Seats ids={s.assignee_seat_ids} people={people} />,
    },
    {
      id: "health",
      header: "Health",
      accessor: (s) => boardHealth(s).label,
      sortable: true,
      className: "w-28",
      cell: (s) => {
        const h = boardHealth(s);
        return (
          <Badge variant={h.tone} dot>
            {h.label}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "",
      accessor: () => null,
      className: "w-10",
      cell: (s) => {
        const b = boardOf(s.board_id);
        return b ? (
          <BoardMenu
            api={api}
            company={company}
            board={b}
            trigger={
              <button
                type="button"
                aria-label={`Actions for ${s.name}`}
                data-slot="board-row-menu"
                className="relative z-10 flex size-7 items-center justify-center rounded-md text-faint opacity-0 outline-none group-hover/board-row:opacity-100 group-focus-within/board-row:opacity-100 hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-ring pointer-coarse:opacity-100"
                onClick={(e) => e.stopPropagation()}
              >
                <EllipsisIcon aria-hidden="true" className="size-4" />
              </button>
            }
          />
        ) : null;
      },
    },
  ];

  /** BOARD-ORDER: drag a row before another (its space, or another one's). */
  const rowProps = (s: BoardSummary) => ({
    className: cn(
      "group/board-row",
      dropBefore === s.board_id &&
        dragging !== s.board_id &&
        "shadow-[inset_0_2px_0_0_var(--primary)]",
      dragging === s.board_id && "opacity-40",
    ),
    ...(sort === null
      ? {
          draggable: true,
          onDragStart: (e: DragEvent<HTMLTableRowElement>) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("application/x-companyos-board", s.board_id);
            setDragging(s.board_id);
          },
          onDragEnd: () => {
            setDragging(null);
            setDropBefore(null);
          },
          onDragOver: (e: DragEvent<HTMLTableRowElement>) => {
            if (!dragging) return;
            e.preventDefault();
            setDropBefore(s.board_id);
          },
          onDrop: (e: DragEvent<HTMLTableRowElement>) => {
            if (!dragging) return;
            e.preventDefault();
            const b = boardOf(dragging);
            if (b && dragging !== s.board_id)
              writes.move(b, s.space_id, s.board_id, spaceOf(s.space_id)?.name);
            setDragging(null);
            setDropBefore(null);
          },
        }
      : {}),
  });

  const loading = summaries.isPending || boards.isPending || spaces.isPending;
  return (
    <div data-screen="work-home" className="flex min-h-full min-w-0 flex-col">
      <LocationHeader
        icon={LayoutDashboardIcon}
        title="Boards"
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <PlusIcon aria-hidden="true" />
            New board
          </Button>
        }
      />
      <div className={BODY}>
        {operatorOnly ? (
          <OperatorOnlyNotice slug={company.slug} className="my-4 max-w-2xl" />
        ) : (
          <StatGrid className="my-4" aria-label="My work today" data-slot="work-today">
            <StatTile
              label="Due today"
              icon={CalendarCheckIcon}
              value={mine.data ? dueToday.length : "…"}
              detail="on your seats"
            />
            <StatTile
              label="Overdue"
              icon={CircleAlertIcon}
              value={mine.data ? overdue.length : "…"}
              detail={overdue.length ? "needs a new date or a push" : "nothing slipped"}
            />
            <StatTile
              label="Due this week"
              icon={CalendarClockIcon}
              value={mine.data ? dueWeek.length : "…"}
              detail={`through ${shortDay(addDays(today, 6))}`}
            />
            <StatTile
              label="Open on my seats"
              icon={ListTodoIcon}
              value={mine.data ? open.length : "…"}
              detail={`${(summaries.data ?? []).filter((s) => !s.archived_at).length} boards in all`}
              href={`/c/${company.slug}/work/my`}
            />
          </StatGrid>
        )}

        <Toolbar aria-label="Boards tools">
          <SearchField
            value={search}
            placeholder="Filter boards…"
            aria-label="Filter boards"
            onChange={(e) => setSearch(e.target.value)}
          />
          <NativeSelect
            size="sm"
            aria-label="Space"
            className="w-40 shrink-0"
            value={spaceId}
            onChange={(e) => setSpaceId(e.target.value)}
          >
            <NativeSelectOption value="">All spaces</NativeSelectOption>
            {spaceList
              .filter((s) => !s.archived_at && s.kind !== "personal")
              .map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {s.name}
                </NativeSelectOption>
              ))}
          </NativeSelect>
          <NativeSelect
            size="sm"
            aria-label="Lead"
            className="w-40 shrink-0"
            value={lead}
            onChange={(e) => setLead(e.target.value)}
          >
            <NativeSelectOption value="">Any lead</NativeSelectOption>
            {leads.map((l) => (
              <NativeSelectOption key={l.id} value={l.id}>
                {l.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          {sort !== null ? (
            <Button variant="ghost" size="sm" onClick={() => setSort(null)}>
              Board order
            </Button>
          ) : null}
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums" aria-live="polite">
            {summaries.data ? `${rows.length} board${rows.length === 1 ? "" : "s"}` : ""}
          </span>
        </Toolbar>

        {loading ? (
          <div role="status" aria-label="Loading the boards" className="mt-2 flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : summaries.isError ? (
          <LoadError what="The boards" error={summaries.error} />
        ) : rows.length ? (
          <TableView
            label="Boards"
            data-slot="boards-overview"
            className="mt-2"
            rows={rows}
            columns={columns}
            getRowId={(s) => s.board_id}
            sort={sort}
            onSortChange={setSort}
            rowProps={rowProps}
            onRowOpen={(s) =>
              void navigate({
                to: "/c/$slug/work/b/$boardId",
                params: { slug: company.slug, boardId: s.board_id },
              })
            }
            emptyTitle="No board matches"
          />
        ) : (
          <EmptyState
            variant="compact"
            icon={SquareKanbanIcon}
            title={search || spaceId || lead ? "No board matches" : "No boards yet"}
            description={
              search || spaceId || lead
                ? "Clear the filters to see every board."
                : "A board holds a space's tasks with its statuses and views. New board makes one from a template."
            }
            actions={
              <Button variant="secondary" onClick={() => setCreating(true)}>
                <PlusIcon aria-hidden="true" />
                New board
              </Button>
            }
          />
        )}

        {operatorOnly ? null : (
          <section aria-label="My work today" className="mt-8 min-w-0">
            <div className="flex h-8 items-center gap-2 px-1">
              <h2 className="font-heading text-[15px] leading-[22px] font-semibold text-foreground">
                My work today
              </h2>
              <Link
                to="/c/$slug/work/my"
                params={{ slug: company.slug }}
                search={keepFocus}
                className="ml-auto text-xs text-primary-text hover:underline"
              >
                All my tasks
              </Link>
            </div>
            {mine.isPending ? (
              <div role="status" aria-label="Loading your tasks" className="flex flex-col gap-2">
                <Skeleton className="h-9" />
                <Skeleton className="h-9" />
              </div>
            ) : mine.isError ? (
              <LoadError what="Your tasks" error={mine.error} />
            ) : (
              <TaskList
                aria-label="My work today"
                api={api}
                company={company}
                taskId={undefined}
                onOpenTask={onOpenTask}
                boardOf={boardOf}
                locationOf={(id) => {
                  const b = boardOf(id);
                  const s = spaceOf(b?.space_id ?? "");
                  return b ? (s ? `${s.name} › ${b.name}` : b.name) : "";
                }}
                header={false}
                groups={[
                  { id: "today", title: "Today", tasks: dueToday.slice(0, 8) },
                  { id: "overdue", title: "Overdue", tasks: overdue.slice(0, 8) },
                ].filter((g) => g.tasks.length)}
                empty={
                  <EmptyState
                    variant="compact"
                    icon={ListTodoIcon}
                    title="Nothing due today"
                    description="Tasks on your seats due today or overdue show here."
                  />
                }
              />
            )}
          </section>
        )}
      </div>
      {creating ? (
        <NewBoardDialog
          api={api}
          company={company}
          spaces={spaceList.filter((s) => s.kind !== "personal")}
          open={creating}
          onOpenChange={setCreating}
        />
      ) : null}
    </div>
  );
}

/**
 * Who leads a board: the lead the board names (BOARD-ORDER), else its space's team lead (the
 * API's `lead_seat_id`). Nobody is made lead by having the most tasks: the picker says "+ Lead".
 */
export function leadOf(s: Pick<BoardSummary, "lead_seat_id">): string | null {
  return s.lead_seat_id;
}

/** "Today", "Tomorrow", or the short date. */
export function nextDueLabel(iso: string, today: string): string {
  const day = localDay(iso);
  if (day === today) return "Today";
  if (day === addDays(today, 1)) return "Tomorrow";
  return shortDay(day);
}

/** The seats on a board's open tasks as a stack of the people holding them (names on hover). */
function Seats({ ids, people }: { ids: readonly string[]; people: SeatPeople }) {
  if (!ids.length) return <span className="text-faint">—</span>;
  return (
    <AvatarStack
      size="sm"
      items={ids.map((id) => {
        const p = people.of(id);
        return { name: p.name, kind: p.de ? "agent" : "person", title: p.full };
      })}
    />
  );
}
